import { AlertsService } from './../../../../services/alerts.service';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {CopilotWizardTileComponent} from "../../components/copilot-wizard-tile/copilot-wizard-tile.component";
import {CopilotMoreWizardOptionsTileComponent} from "../../components/copilot-more-wizard-options-tile/copilot-more-wizard-options-tile.component";
import {CopilotPromptBarComponent} from "../../components/copilot-prompt-bar/copilot-prompt-bar.component";
import {FormControl} from '@angular/forms';
import {CopilotService} from '../../../../services/copilot.service';
import {TwoStageBrandPickerComponent} from "../../components/two-stage-brand-picker/two-stage-brand-picker.component";
import { Wizard } from '../../types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { StateService } from '../../../../services/state.service';
import { WizardComponent } from '../../components/wizard/wizard.component';
import { Router } from '@angular/router';
import { RoutePaths } from '../../../../shared/types/common';
import { MatTooltip } from '@angular/material/tooltip';
import { tooltips } from '../../../../utils/tooltips';
import { PosthogService } from '../../../../services/posthog.service';
import { PosthogEventName, PosthogEventCode } from '../../../../services/types';
import { AuthorizationService } from '../../../../services/authorization.service';

const PolarisToolNameMap: Record<string, string> = {
  'alert_center': 'Alert Center',
  'creative_brief': 'Creative Brief',
  'campaign_review': 'Campaign Review',
  'audience_research': 'Audience Research',
  'data_snapshot': 'Data Snapshot',
};

const PolarisToolKeyMap: Record<string, string> = Object.fromEntries(
  Object.entries(PolarisToolNameMap).map(([key, value]) => [value, key])
);

const NUMBER_OF_WIZARD_TILES = 4;

@Component({
  selector: 'app-copilot-home',
  imports: [
    CopilotWizardTileComponent,
    CopilotMoreWizardOptionsTileComponent,
    CopilotPromptBarComponent,
    TwoStageBrandPickerComponent,
    NgForOf,
    WizardComponent,
    MatTooltip,
    CommonModule
  ],
  templateUrl: './copilot-home.component.html',
  styleUrl: './copilot-home.component.scss'
})

export class CopilotHomeComponent implements OnInit {
  public promptControl = new FormControl('');
  private authorizationService = inject(AuthorizationService);
  private alertsService = inject(AlertsService);
  private copilotService = inject(CopilotService);
  private destroyRef = inject(DestroyRef);
  private stateService = inject(StateService);
  private router = inject(Router);
  private posthogService = inject(PosthogService);
  public isWizardOpen: boolean = false;
  public userFirstName = '';
  public wizards: Wizard[] = [];
  public numberOfWizardTiles: number = NUMBER_OF_WIZARD_TILES;
  public selectedWizardIndex: number = 0;
  protected readonly tooltips = tooltips;
  protected hasAlertCenterPermissions: boolean = false;

  ngOnInit(): void {
    this.stateService.loading = true;
    this.userFirstName = this.copilotService.customerName;
    this.copilotService.myCompetitors.set([]);
    this.copilotService.myBrand.set(null);
    this.copilotService.getWizardsResponse().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(wizards => {
      this.wizards = wizards;
      this.stateService.loading = false;
    });
    this.hasAlertCenterPermissions = this.alertsService.hasPermissions();
  }

  openAlertCenter(): void {
    if (this.hasAlertCenterPermissions) {
      this.posthogService.capture(PosthogEventName.POLARIS_TOOL_SELECTED, {
        [PosthogEventCode.TOOL_NAME]: 'alert_center'
      });
      this.router.navigate([RoutePaths.ALERT_CENTER]);
    }
  }

  openWizard(id: number): void {
    if (this.isExpiredUser) return;
    const wizardTitle = this.wizards[id]?.title ?? '';
    const toolKey = PolarisToolKeyMap[wizardTitle] ?? wizardTitle;
    this.posthogService.capture(PosthogEventName.POLARIS_TOOL_SELECTED, {
      [PosthogEventCode.TOOL_NAME]: toolKey
    });
    this.isWizardOpen = true;
    this.selectedWizardIndex = id;
  }

  closeWizard(): void {
    if (this.isExpiredUser) return;
    this.isWizardOpen = false;
    this.selectedWizardIndex = 0;
  }

  onSendMessage(message: string): void {
    if (this.isExpiredUser) return;
    this.copilotService.triggerMessage(message, {origin: 'polaris_homepage'});
  }

  get isExpiredUser(): boolean {
    return this.authorizationService.isExpiredUser;
  }

}
